/* @flow */
import { observable, computed } from 'mobx';

import { ApiModel } from '../api/ApiModel';

const PASSWORD_MIN: number = 8;

export class AccountModel extends ApiModel {
  //@observable regionId: number = 1;

  @observable localState = {
    formIsLoading: false,
    showResponseMessage: false,
    responseMessage: '',
  };

  @observable firstName: string = '';

  @observable lastName: string = '';

  @observable countyState: string = '';

  @observable company: string = '';

  @observable country: string = '';

  @observable roleId: number = 1;

  @observable accountNumbers: string = '';

  @observable additionalInfo: string = '';

  @observable phoneNumber: string = '';

  @observable email: string = '';

  @observable termsAgreed: boolean = false;

  passwordModel: PasswordModel = new PasswordModel(this.api);

  @computed get requiredNonBlank(): boolean {
    return this.firstName.length > 0 && this.lastName.length > 0 && this.email.length > 0;
  }

  @computed get valid(): boolean {
    return (
      this.requiredNonBlank &&
      this.email.includes('@') &&
      this.termsAgreed &&
      this.passwordModel.passwordValid
    );
  }

  createAccount(emailModel: EmailModel): Promise<*> {
    emailModel.email = this.email;
    return this.api.createResourceGuest('users/register', {
      first_name: this.firstName,
      last_name: this.lastName,
      email: this.email,
      password: this.passwordModel.password1,
      password_confirmation: this.passwordModel.password1,
    });
  }
}

export class PasswordModel extends ApiModel {
  passwordMinimumLength: number = PASSWORD_MIN;

  @observable password1 = '';

  @observable password2 = '';

  @computed
  get passwordsMatch(): boolean {
    return this.password1 === this.password2;
  }

  @computed
  get passwordNonBlank(): boolean {
    return this.password1 !== '' || this.password2 !== '';
  }

  @computed
  get passwordLengthOK(): boolean {
    return this.password1.length >= PASSWORD_MIN && this.password1.length >= PASSWORD_MIN;
  }

  @computed
  get passwordValid(): boolean {
    return this.passwordLengthOK && this.passwordNonBlank && this.passwordsMatch;
  }

  get password(): string {
    return this.password1;
  }

  /*
    NIST Special Publication 800-63B (Revision 3) mentions the following about password rules:

    Verifiers SHOULD NOT impose other composition rules (e.g. requiring mixtures of different
    character types or prohibiting consecutively repeated characters) for memorized secrets.

    see: https://pages.nist.gov/800-63-3/sp800-63b.html

    https://stackoverflow.com/questions/48345922/reference-password-validation
    */

  resetPassword(email: string, token: string): Promise<*> {
    return this.api.createResourceGuest('password/reset', {
      password: this.password1,
      token,
      password_confirmation: this.password2,
      email,
    });
  }
}

export class EmailModel extends ApiModel {
  @observable email: string = '';
  @observable loading = false;

  @computed get valid(): boolean {
    return this.email.includes('@');
  }

  resetData() {
    this.loading = false;
  }

  async forgotPassword(): Promise<*> {
    //avoid cyclical references serializing 'this'
    this.loading = true;
    const response = await this.api.createResourceGuest('password/forgot', { email: this.email });
    this.loading = false;

    return response;
  }

  requestMagicLink(): Promise<*> {
    return this.api.createResourceGuest('magic-token/request', { email: this.email });
  }

  async validateMagicLink(credentials: { email: string, token: string }): Promise<*> {
    return this.api.loginWithMagicLink(
      await this.api.createResourceGuest('magic-token/validate', credentials)
    );
  }

  async validateJwtToken(jwt) {
    this.api.loginWithJwtToken(await this.api.createResourceGuest('/b2c-token/validate', jwt));
  }
}

export type TypeWithEmailModel = {| emailModel: EmailModel |};
